import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, Music, Headphones } from "lucide-react";

const platformConfig = {
  spotify: {
    name: "Spotify",
    color: "#1DB954",
    bgColor: "bg-green-500/10",
    borderColor: "border-green-500/30",
  },
  apple_music: {
    name: "Apple Music",
    color: "#FC3C44",
    bgColor: "bg-red-500/10",
    borderColor: "border-red-500/30",
  },
  amazon_music: {
    name: "Amazon Music",
    color: "#00A8E1",
    bgColor: "bg-blue-500/10",
    borderColor: "border-blue-500/30",
  },
};

const formatNumber = (num) => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + "M";
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + "K";
  }
  return num.toString();
};

const MetricCard = ({ platform, data, index }) => {
  const config = platformConfig[platform] || {
    name: platform,
    color: "#39FF14",
    bgColor: "bg-meta-neon-dim",
    borderColor: "border-meta-neon/30",
  };

  const isPositiveGrowth = data.growth_percent > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className={`${config.bgColor} border ${config.borderColor} p-6`}
      data-testid={`stream-metric-${platform}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Headphones size={18} style={{ color: config.color }} />
          <span className="font-mono text-xs uppercase text-meta-text-muted">
            {config.name}
          </span>
        </div>
        <div
          className={`flex items-center gap-1 text-xs font-mono ${
            isPositiveGrowth ? "text-meta-neon" : "text-meta-danger"
          }`}
        >
          {isPositiveGrowth ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
          <span>{isPositiveGrowth ? "+" : ""}{data.growth_percent}%</span>
        </div>
      </div>

      {/* Monthly Listeners */}
      <div className="mb-4">
        <p
          className="font-mono text-3xl font-bold tabular-nums"
          style={{ color: config.color }}
        >
          {formatNumber(data.monthly_listeners)}
        </p>
        <p className="text-meta-text-muted text-xs uppercase tracking-wider mt-1">
          Monthly Listeners
        </p>
      </div>

      {/* Total Streams */}
      <div className="pt-4 border-t border-meta-border/50">
        <div className="flex justify-between items-center">
          <span className="text-meta-text-muted text-xs font-mono">
            Total Streams
          </span>
          <span className="text-meta-text font-mono font-medium">
            {formatNumber(data.total_streams)}
          </span>
        </div>
      </div>

      {/* Top Track */}
      <div className="mt-3 flex items-center gap-2 text-xs">
        <Music size={12} className="text-meta-text-muted" />
        <span className="text-meta-text-muted">Top:</span>
        <span className="text-meta-text font-medium truncate">
          {data.top_track}
        </span>
      </div>
    </motion.div>
  );
};

export const StreamMetrics = ({ platforms }) => {
  if (!platforms || platforms.length === 0) {
    return (
      <div className="bg-meta-surface border border-meta-border p-8 text-center">
        <Headphones className="mx-auto mb-3 text-meta-text-muted" size={32} />
        <p className="text-meta-text-muted">Loading stream metrics...</p>
      </div>
    );
  }

  // Calculate totals
  const totalMonthlyListeners = platforms.reduce(
    (sum, p) => sum + p.monthly_listeners,
    0
  );
  const totalStreams = platforms.reduce((sum, p) => sum + p.total_streams, 0);

  return (
    <div data-testid="stream-metrics">
      {/* Header with totals */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-mono text-xs uppercase tracking-wider text-meta-text-muted">
            Global Stream Tracker
          </h2>
          <p className="font-sans font-semibold text-meta-text text-lg mt-1">
            Streaming Analytics
          </p>
        </div>
        <div className="flex gap-6">
          <div className="text-right">
            <p className="font-mono text-2xl font-bold text-meta-neon tabular-nums">
              {formatNumber(totalMonthlyListeners)}
            </p>
            <p className="text-meta-text-muted text-xs uppercase">
              Total Monthly Listeners
            </p>
          </div>
          <div className="text-right">
            <p className="font-mono text-2xl font-bold text-meta-text tabular-nums">
              {formatNumber(totalStreams)}
            </p>
            <p className="text-meta-text-muted text-xs uppercase">
              All-Time Streams
            </p>
          </div>
        </div>
      </div>

      {/* Platform Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {platforms.map((platform, index) => (
          <MetricCard
            key={platform.platform}
            platform={platform.platform}
            data={platform}
            index={index}
          />
        ))}
      </div>
    </div>
  );
};
